// ═══════════════════════════════════════════════════════════════════════
//   CADASTRO DE ALUNO — TELA ÚNICA (Single Screen)
//   Substitui o wizard por etapas. Todos os blocos numa só tela,
//   mesmo payload / mesmos endpoints (POST|PUT /api/students).
// ═══════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  User as UserIcon, Users, HeartPulse, FileText, GraduationCap, Wallet,
  Save, X, RotateCcw, Loader2, ChevronDown, AlertCircle, CheckCircle2, Trash2,
  FileSignature, Sparkles, FileSearch, Home, ShieldAlert,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  ResponsaveisStep, FinanceiroStep, HistoricoCard,
  maskCPF, maskPhone, maskCEP, fetchCEP, calcAge, validEmail, institutionalEmail,
} from './StudentWizard';
import ContratosBlock from './ContratosBlock';
import FinanceiroContratosBlock from './FinanceiroContratosBlock';
import DocumentosDnaSignDialog from './DocumentosDnaSignDialog';
import SmartImportModal from '@/pages/Matriculas/SmartImportModal';
import { StudentPhotoUpload } from './StudentPhotoUpload';
import { usePermV2 } from '@/lib/perm_v2';
import OcorrenciasPanel from '@/pages/PortalGestao/OcorrenciasPanel';

const API_URL = window.location.origin;
const DRAFT_KEY = 'student-single-draft-new';
const STATUS_OPTIONS = ['Matriculado', 'Ativo', 'Inativo', 'Trancado', 'Transferido', 'Concluído'];
const REACTIVATION_STATUSES = ['Inativo', 'Trancado', 'Transferido', 'Concluído'];

const BLOCKS = [
  { id: 'aluno',        label: 'Dados do aluno',      icon: UserIcon,      hint: 'Identificação, contato, endereço e documentos' },
  { id: 'contratos',    label: 'Contratos e matrículas', icon: FileSignature, hint: 'Histórico contratual e acadêmico', editOnly: true },
  { id: 'saude',        label: 'Saúde e segurança',   icon: HeartPulse,    hint: 'Cuidados e emergências' },
  { id: 'pei',          label: 'PEI e pedagógico',    icon: GraduationCap, hint: 'Acompanhamento individual' },
  { id: 'ocorrencias',  label: 'Ocorrências',         icon: ShieldAlert,   hint: 'Histórico pedagógico e providências', editOnly: true },
  { id: 'responsaveis', label: 'Responsáveis',        icon: Users,         hint: 'Contatos e vínculos da família' },
  { id: 'financeiro',   label: 'Financeiro',          icon: Wallet,        hint: 'Planos e títulos' },
];
const B = Object.fromEntries(BLOCKS.map(b => [b.id, b]));

// V9.14 · sub-título dos blocos internos do quadro único "Dados do aluno"
const SubTitle = ({ children }) => (
  <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-violet-700 border-b border-violet-100 pb-1 mb-3">
    {children}
  </p>
);

const FieldError = ({ children }) => (
  <p className="text-[11px] text-rose-600 mt-1 flex items-center gap-1">
    <AlertCircle className="w-3 h-3" />{children}
  </p>
);

const fmtDateBR = (d) => {
  if (!d) return '—';
  const p = String(d).slice(0, 10).split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : String(d);
};

const emptyForm = () => ({ situation: 'Matriculado', nationality: 'Brasileiro(a)', is_foreigner: false, active: true });

// ─── Card de bloco (colapsável, tudo na mesma tela) ────────────────────
const Block = ({ block, openState, onToggle, innerRef, badge, children }) => {
  const Icon = block.icon;
  const isOpen = openState;
  return (
    <section
      ref={innerRef}
      className="scroll-mt-4 rounded-2xl border border-slate-200/90 bg-white shadow-[0_1px_2px_rgba(15,23,42,.04)] overflow-hidden"
      data-testid={`block-${block.id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50/70 transition-colors"
        data-testid={`block-toggle-${block.id}`}
      >
        <div className="w-10 h-10 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center shrink-0 ring-1 ring-violet-100">
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base font-bold text-slate-900 leading-tight">{block.label}</p>
          <p className="text-xs text-slate-500">{block.hint}</p>
        </div>
        {badge}
        <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="px-5 pb-6 pt-1 border-t border-slate-100">
          {children}
        </div>
      )}
    </section>
  );
};

export default function StudentSinglePage({
  open, onOpenChange, mode = 'new', student = null,
  classGroups = [], responsibles = [], onSaved, onResponsiblesUpdate,
}) {
  const [form, setForm] = useState(emptyForm());
  const { financeiroVisivel } = usePermV2();
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [errors, setErrors] = useState({});
  const [openBlocks, setOpenBlocks] = useState(() => BLOCKS.reduce((a, b) => ({ ...a, [b.id]: true }), {}));
  const [draftSaved, setDraftSaved] = useState(false);
  const [historico, setHistorico] = useState(null);
  const [historicoLoading, setHistoricoLoading] = useState(false);
  const [historicoDismissed, setHistoricoDismissed] = useState(false);
  const [focusContractId, setFocusContractId] = useState(null);
  const [docsOpen, setDocsOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const bodyRef = useRef(null);
  const blockRefs = useRef({});
  const set = useCallback((k, v) => setForm(f => ({ ...f, [k]: v })), []);

  // ─── Carga inicial ───────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setHistorico(null);
    setHistoricoDismissed(false);
    setDraftSaved(false);
    if (mode === 'edit' && student) {
      setForm({ ...student });
      // Garante cadastro completo: recarrega o registro inteiro do backend
      if (student.id) {
        (async () => {
          try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/students/${student.id}`, { headers: { Authorization: `Bearer ${token}` } });
            if (res.ok) {
              const full = await res.json();
              setForm(f => ({ ...full, ...Object.fromEntries(Object.entries(f).filter(([, v]) => v !== null && v !== undefined && v !== '')) }));
            }
          } catch { /* mantém dados recebidos */ }
        })();
      }
    } else {
      try {
        const raw = localStorage.getItem(DRAFT_KEY);
        setForm(raw ? { ...emptyForm(), ...JSON.parse(raw) } : emptyForm());
      } catch { setForm(emptyForm()); }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, student?.id]);

  // ─── Rascunho automático (somente novo cadastro) ─────────────────────
  useEffect(() => {
    if (!open || mode === 'edit') return;
    const t = setTimeout(() => {
      try { localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); setDraftSaved(true); } catch { /* noop */ }
    }, 600);
    return () => clearTimeout(t);
  }, [form, open, mode]);

  // ─── Histórico anterior (reativação de ex-aluno) ─────────────────────
  useEffect(() => {
    if (!open || mode !== 'edit' || !student?.id) return;
    if (!REACTIVATION_STATUSES.includes(student.situation)) return;
    const run = async () => {
      setHistoricoLoading(true);
      try {
        const year = (student.year || new Date().getFullYear()) - 1;
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/students/historico-anterior?student_id=${student.id}&year=${year}&with_ai=true`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res.ok ? await res.json() : null;
        setHistorico(data?.found ? data : null);
      } catch { setHistorico(null); } finally { setHistoricoLoading(false); }
    };
    run();
  }, [open, mode, student?.id, student?.situation, student?.year]);

  const idade = useMemo(() => calcAge(form.birth_date), [form.birth_date]);
  const turma = useMemo(() => classGroups.find(c => c.id === form.class_group_id), [classGroups, form.class_group_id]);

  // ─── E-mail institucional automático (nome + nascimento) ─────────────
  const autoEmail = useMemo(() => institutionalEmail(form.name, form.birth_date), [form.name, form.birth_date]);
  useEffect(() => {
    if (!open || form.email_manual_override) return;
    if (autoEmail && form.email !== autoEmail) set('email', autoEmail);
  }, [open, autoEmail, form.email_manual_override, form.email, set]);

  // ─── Matrícula: obrigatória, única e permanente ──────────────────────
  const [regCheck, setRegCheck] = useState(null);
  useEffect(() => {
    if (!open) return;
    const reg = (form.registration_number || '').toString().trim();
    if (!reg) { setRegCheck(null); return; }
    const t = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const url = `${API_URL}/api/registration-number/check?value=${encodeURIComponent(reg)}`
          + (mode === 'edit' && student?.id ? `&student_id=${student.id}` : '');
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setRegCheck(await res.json());
      } catch { /* silencioso */ }
    }, 450);
    return () => clearTimeout(t);
  }, [open, mode, student?.id, form.registration_number]);

  // Novo cadastro sem matrícula: sugere a próxima livre (nunca reutiliza número)
  useEffect(() => {
    if (!open || mode === 'edit' || (form.registration_number || '').toString().trim()) return;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/registration-number/next`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        setForm(f => (f.registration_number ? f : { ...f, registration_number: data.value }));
      } catch { /* silencioso */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  // ─── Origem dos dados por campo (documento que preencheu) ───────────
  const [origins, setOrigins] = useState({});
  useEffect(() => {
    if (!open || mode !== 'edit' || !student?.id) { setOrigins({}); return; }
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/smart-import/origins/student/${student.id}`,
          { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        setOrigins(Object.fromEntries((data.items || []).map(o => [o.field, o])));
      } catch { /* silencioso */ }
    })();
  }, [open, mode, student?.id]);

  const Origem = ({ field }) => {
    const o = origins[field];
    if (!o) return null;
    const quando = o.confirmed_at ? new Date(o.confirmed_at).toLocaleDateString('pt-BR') : '—';
    return (
      <button type="button" data-testid={`origem-campo-${field}`}
        title={`Origem: ${o.filename || o.doc_type_label || 'documento'} · página ${o.page || 1} · conferido em ${quando}`}
        className="ml-1 inline-flex align-middle text-violet-600 hover:text-violet-800"
        onClick={() => toast.info(`${o.label}: ${o.value}`, {
          description: `Origem: ${o.filename || o.doc_type_label || 'documento'} · página ${o.page || 1} · conferido em ${quando}${o.manual_edit ? ' · ajustado manualmente' : ''}`,
        })}>
        <FileSearch className="w-3.5 h-3.5" />
      </button>
    );
  };

  const goToBlock = useCallback((id) => {
    setOpenBlocks(s => ({ ...s, [id]: true }));
    setTimeout(() => {
      blockRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  }, []);

  // ─── Validação · obrigatórios: nome, nascimento e e-mail (erro no campo) ──
  const validate = () => {
    const e = {};
    if (!form.name?.trim()) e.name = 'Informe o nome completo do aluno';
    const bd = (form.birth_date || '').slice(0, 10);
    if (!bd) e.birth_date = 'Informe a data de nascimento';
    else if (Number.isNaN(new Date(bd).getTime()) || new Date(bd) > new Date()) e.birth_date = 'Data de nascimento inválida';
    if (!form.registration_number?.toString().trim()) e.registration_number = 'Informe o número da matrícula.';
    else if (regCheck && regCheck.value === form.registration_number.toString().trim() && regCheck.available === false) {
      e.registration_number = regCheck.message;
    }
    if (!form.email?.trim()) e.email = 'Informe o e-mail';
    else if (!validEmail(form.email)) e.email = 'E-mail inválido';
    return e;
  };

  const applyHistorico = () => {
    if (!historico?.formatted_summary) return;
    const current = (form.pedagogical_notes || '').trim();
    set('pedagogical_notes', current ? `${current}\n\n---\n${historico.formatted_summary}` : historico.formatted_summary);
    setHistoricoDismissed(true);
    toast.success(`Histórico de ${historico.year} importado`);
  };

  // ─── Recarregar dados do aluno ───────────────────────────────────────
  const handleReload = async () => {
    if (mode !== 'edit' || !student?.id) {
      setForm(emptyForm());
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ }
      setErrors({});
      toast.success('Formulário limpo');
      return;
    }
    setReloading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/students/${student.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Não foi possível recarregar');
      setForm(await res.json());
      setErrors({});
      onResponsiblesUpdate?.();
      toast.success('Dados recarregados do servidor');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setReloading(false);
    }
  };

  // ─── Salvar ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) {
      goToBlock('aluno');
      setTimeout(() => {
        const first = Object.keys(e)[0];
        const el = document.querySelector(`[data-testid="single-${first.replace(/_/g, '-')}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el?.focus?.();
      }, 200);
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
      const payload = {
        ...form,
        registration_number: form.registration_number || '',
        class_name: form.class_name || (turma ? turma.name : ''),
        active: form.active !== false,
        is_foreigner: !!form.is_foreigner,
        no_email: !!form.no_email,
        no_sms: !!form.no_sms,
        year: form.year ? Number(form.year) : new Date().getFullYear(),
      };
      const res = await fetch(
        mode === 'edit' ? `${API_URL}/api/students/${student.id}` : `${API_URL}/api/students`,
        { method: mode === 'edit' ? 'PUT' : 'POST', headers, body: JSON.stringify(payload) },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const d = err.detail;
        const msg = typeof d === 'string' ? d : (d?.message || err.message || 'Erro ao salvar cadastro');
        if (d?.error === 'MATRICULA_DUPLICADA') setErrors((e) => ({ ...e, registration_number: d.message }));
        throw new Error(msg);
      }
      const saved = await res.json();

      // Regra opcional: aplicar endereço do aluno aos responsáveis sem endereço
      if (form.apply_aluno_address_to_responsibles) {
        const REQ = ['zip_code', 'address', 'address_number', 'neighborhood', 'city', 'state'];
        if (REQ.every(k => (form[k] || '').toString().trim())) {
          const ids = [...new Set([form.responsible_id, form.responsible_didactic_id, form.responsible_financial_optional_id].filter(Boolean))];
          for (const rid of ids) {
            const r = (responsibles || []).find(x => x.id === rid);
            if (!r || !REQ.some(k => !((r[k] || '').toString().trim()))) continue;
            await fetch(`${API_URL}/api/responsibles/${rid}`, {
              method: 'PUT',
              headers,
              body: JSON.stringify({
                ...r,
                zip_code: r.zip_code || form.zip_code,
                address: r.address || form.address,
                address_number: r.address_number || form.address_number,
                complement: r.complement || form.complement,
                neighborhood: r.neighborhood || form.neighborhood,
                city: r.city || form.city,
                state: r.state || form.state,
              }),
            }).catch(() => {});
          }
        }
      }

      if (mode !== 'edit') { try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ } }
      toast.success('Cadastro salvo com sucesso.');
      // V9.7 · salvar NUNCA fecha a tela: mantém bloco aberto, scroll e formulário
      const keepScroll = bodyRef.current?.scrollTop || 0;
      setForm((f) => ({ ...f, ...saved }));
      onSaved?.(saved, { keepOpen: true });
      [0, 120, 350, 800, 1500].forEach((ms) => setTimeout(() => {
        if (bodyRef.current && Math.abs(bodyRef.current.scrollTop - keepScroll) > 40) {
          bodyRef.current.scrollTop = keepScroll;
        }
      }, ms));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const initial = (form.name || '?').trim().charAt(0).toUpperCase();

  const statusBadge = (ok, warnLabel) => (
    <span
      className={`hidden sm:inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${
        ok ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'
      }`}
    >
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
      {ok ? 'Preenchido' : warnLabel}
    </span>
  );

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-6xl w-[97vw] h-[85vh] sm:h-[95vh] p-0 overflow-hidden rounded-2xl border border-violet-100 flex flex-col gap-0"
        data-testid="student-single-page"
      >
        <VisuallyHidden.Root>
          <DialogTitle>{mode === 'edit' ? 'Editar cadastro do aluno' : 'Novo cadastro de aluno'}</DialogTitle>
        </VisuallyHidden.Root>

        {/* ═══ CABEÇALHO DO ALUNO ═══ */}
        <div className="relative shrink-0 bg-gradient-to-br from-violet-800 via-violet-700 to-fuchsia-700 text-white px-5 sm:px-7 pt-5 pb-4 overflow-hidden">
          <div className="pointer-events-none absolute -top-20 -right-14 w-56 h-56 rounded-full bg-emerald-400/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 left-10 w-52 h-52 rounded-full bg-violet-400/30 blur-3xl" />

          <div className="relative flex items-start gap-4 flex-wrap">
            {/* Foto / avatar */}
            <div className="w-16 h-16 rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur-sm overflow-hidden flex items-center justify-center shrink-0 shadow-lg" data-testid="single-avatar">
              {form.photo_url
                ? <img src={form.photo_url} alt={form.name || 'Aluno'} className="w-full h-full object-cover" />
                : <span className="text-2xl font-bold">{initial}</span>}
            </div>

            <div className="flex-1 min-w-[220px]">
              <p className="text-[10px] uppercase tracking-[0.2em] text-violet-100/90 font-semibold">
                {mode === 'edit' ? 'Editar cadastro' : 'Novo cadastro'} · Tela única
              </p>
              <h2 className="text-2xl font-bold leading-tight truncate" data-testid="single-header-name">
                {form.name?.trim() || 'Novo aluno'}
              </h2>
              <div className="flex items-center gap-1.5 mt-2 flex-wrap text-[11px]">
                <span className="rounded-full bg-white/12 border border-white/15 px-2.5 py-1" data-testid="single-header-matricula">
                  Matrícula: <strong>{form.registration_number || '—'}</strong>
                </span>
                <span className="rounded-full bg-emerald-400/20 border border-emerald-300/40 px-2.5 py-1" data-testid="single-header-status">
                  {form.situation || 'Matriculado'}
                </span>
                <span className="rounded-full bg-white/12 border border-white/15 px-2.5 py-1" data-testid="single-header-turma">
                  Turma: <strong>{turma?.name || form.class_name || '—'}</strong>
                </span>
                <span className="rounded-full bg-white/12 border border-white/15 px-2.5 py-1" data-testid="single-header-birth">
                  Nasc.: <strong>{fmtDateBR(form.birth_date)}</strong>
                  {idade != null && <> · {idade} anos</>}
                </span>
                {(form.is_new_student || form.student_origin === 'novos_dnas') && (
                  <span className="rounded-full bg-emerald-400/25 border border-emerald-300/50 px-2.5 py-1 font-semibold"
                        data-testid="single-header-novo">
                    Aluno novo · Matrícula {form.interest_year || form.year || ''} em andamento
                  </span>
                )}
              </div>
            </div>

            {/* Ações do cabeçalho */}
            <div className="flex items-center gap-2 flex-wrap justify-end w-full sm:w-auto sm:shrink-0">
              <button
                type="button" onClick={handleReload} disabled={saving || reloading}
                className="inline-flex items-center gap-1.5 rounded-xl bg-white/12 hover:bg-white/22 border border-white/20 px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-50"
                data-testid="single-reload"
              >
                {reloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                {mode === 'edit' ? 'Recarregar' : 'Limpar'}
              </button>
              <button
                type="button" onClick={() => onOpenChange(false)} disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-xl bg-white/12 hover:bg-white/22 border border-white/20 px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-50"
                data-testid="single-cancel-top"
              >
                <X className="w-3.5 h-3.5" /> Cancelar
              </button>
              {(form.id || student?.id) && (
                <button
                  type="button" onClick={() => setImportOpen(true)} disabled={saving}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-white/12 hover:bg-white/22 border border-white/20 px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-50"
                  data-testid="single-importar-documentos"
                >
                  <Sparkles className="w-3.5 h-3.5" /> Importar documentos
                </button>
              )}
              {mode === 'edit' && student?.id && (
                <button
                  type="button" onClick={() => setDocsOpen(true)} disabled={saving}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-white text-violet-800 hover:bg-violet-50 border border-white/60 px-3.5 py-2 text-xs font-bold shadow-lg shadow-violet-900/20 transition-colors disabled:opacity-50"
                  data-testid="single-documentos-dnasign"
                >
                  <FileText className="w-3.5 h-3.5" /> Documentos DNA Sign
                </button>
              )}
              <button
                type="button" onClick={handleSave} disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white px-4 py-2 text-xs font-bold shadow-lg shadow-emerald-900/25 transition-colors disabled:opacity-60"
                data-testid="single-save-top"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Salvar cadastro
              </button>
            </div>
          </div>

          {/* Atalhos para blocos */}
          <div className="relative mt-4 flex items-center gap-1.5 overflow-x-auto pb-0.5 -mx-1 px-1">
            {BLOCKS.filter(b => (!b.editOnly || mode === 'edit')
              && (financeiroVisivel || !['financeiro', 'contratos'].includes(b.id))).map(b => (
              <button
                key={b.id} type="button" onClick={() => goToBlock(b.id)}
                className="shrink-0 text-[10.5px] uppercase tracking-wider font-bold px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white text-violet-50 hover:text-violet-800 border border-white/15 transition-colors"
                data-testid={`single-jump-${b.id}`}
              >
                {b.label}
              </button>
            ))}
          </div>
        </div>

        {/* ═══ CORPO — TODOS OS BLOCOS NA MESMA TELA ═══ */}
        <div ref={bodyRef} className="flex-1 overflow-y-auto bg-slate-50/70 px-4 sm:px-6 py-5 space-y-4">

          {/* 1 — DADOS DO ALUNO · quadro único (identificação · contato · endereço · documentos) */}
          <Block
            block={B.aluno}
            openState={openBlocks.aluno}
            onToggle={() => setOpenBlocks(s => ({ ...s, aluno: !s.aluno }))}
            innerRef={el => (blockRefs.current.aluno = el)}
            badge={statusBadge(!!form.name?.trim() && !!form.birth_date && !!form.email && !!(form.registration_number || '').toString().trim(), 'Obrigatórios pendentes')}
          >
            <div className="pt-4 space-y-6">
              {/* IDENTIFICAÇÃO */}
              <div>
                <SubTitle>Identificação</SubTitle>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
                  <div className="lg:col-span-1 sm:col-span-2">
                    <Label className="text-xs font-semibold text-slate-700">Nome completo <span className="text-rose-500">*</span></Label>
                    <Input
                      value={form.name || ''} onChange={e => set('name', e.target.value)}
                      placeholder="Ex.: Maria Eduarda Almeida Souza"
                      className={errors.name ? 'border-rose-400 ring-2 ring-rose-100' : ''}
                      data-testid="single-name"
                    />
                    {errors.name && <FieldError>{errors.name}</FieldError>}
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Matrícula <span className="text-rose-500">*</span></Label>
                    <Input value={form.registration_number || ''}
                      onChange={e => set('registration_number', e.target.value)}
                      placeholder="Ex.: 20260142"
                      className={errors.registration_number ? 'border-rose-400 ring-2 ring-rose-100' : ''}
                      data-testid="single-registration" />
                    {errors.registration_number && <FieldError>{errors.registration_number}</FieldError>}
                    {!errors.registration_number && regCheck?.value === (form.registration_number || '').toString().trim() && (
                      regCheck.available
                        ? <p className="text-[10.5px] font-semibold text-emerald-700 mt-0.5" data-testid="single-registration-ok">✓ Matrícula disponível</p>
                        : <p className="text-[10.5px] font-semibold text-amber-700 mt-0.5" data-testid="single-registration-dup">⚠ Matrícula já utilizada por: {regCheck.student_name}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Status</Label>
                    <Select value={form.situation || 'Matriculado'} onValueChange={v => set('situation', v)}>
                      <SelectTrigger data-testid="single-situation"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Turma</Label>
                    <Select value={form.class_group_id || ''} onValueChange={v => set('class_group_id', v)}>
                      <SelectTrigger data-testid="single-class"><SelectValue placeholder="Selecione a turma" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {classGroups.map(cg => <SelectItem key={cg.id} value={cg.id}>{cg.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Data de nascimento <span className="text-rose-500">*</span></Label>
                    <Input
                      type="date" value={(form.birth_date || '').slice(0, 10)}
                      onChange={e => set('birth_date', e.target.value)}
                      className={errors.birth_date ? 'border-rose-400 ring-2 ring-rose-100' : ''}
                      data-testid="single-birth-date"
                    />
                    {errors.birth_date
                      ? <FieldError>{errors.birth_date}</FieldError>
                      : <p className="text-[10.5px] text-slate-400 mt-0.5">{idade != null ? `${idade} anos (calculado)` : 'Idade calculada automaticamente'}</p>}
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Gênero</Label>
                    <Select value={form.gender || ''} onValueChange={v => set('gender', v)}>
                      <SelectTrigger data-testid="single-gender"><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>{['Feminino', 'Masculino', 'Outro', 'Prefere não informar'].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Nacionalidade</Label>
                    <Input value={form.nationality || ''} onChange={e => set('nationality', e.target.value)} placeholder="Brasileiro(a)" data-testid="single-nationality" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Naturalidade</Label>
                    <Input value={form.birth_city || ''} onChange={e => set('birth_city', e.target.value)} placeholder="Cidade de nascimento" data-testid="single-birth-city" />
                  </div>
                </div>
              </div>

              {/* CONTATO */}
              <div>
                <SubTitle>Contato</SubTitle>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">
                      E-mail <span className="text-rose-500">*</span>
                      <span className="ml-1 font-normal text-slate-400">institucional automático</span>
                      <Origem field="email" />
                    </Label>
                    <Input
                      type="email" value={form.email || ''}
                      onChange={e => setForm(f => ({ ...f, email: e.target.value, email_manual_override: true }))}
                      placeholder="primeironome00000000@novodna.com.br"
                      className={errors.email ? 'border-rose-400 ring-2 ring-rose-100' : ''}
                      data-testid="single-email"
                    />
                    {form.email_manual_override && !!autoEmail && (
                      <button type="button" data-testid="single-email-restore"
                        className="mt-1 text-[10.5px] font-semibold text-violet-700 hover:underline"
                        onClick={() => setForm(f => ({ ...f, email: autoEmail, email_manual_override: false }))}>
                        Restaurar e-mail automático ({autoEmail})
                      </button>
                    )}
                    {errors.email && <FieldError>{errors.email}</FieldError>}
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Telefone principal<Origem field="phone" /></Label>
                    <Input value={form.phone || ''} onChange={e => set('phone', maskPhone(e.target.value))} placeholder="(11) 3333-4444" data-testid="single-phone" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Celular / WhatsApp<Origem field="cellphone" /></Label>
                    <Input value={form.cellphone || ''} onChange={e => set('cellphone', maskPhone(e.target.value))} placeholder="(11) 98888-7777" data-testid="single-cellphone" />
                  </div>
                </div>
              </div>

              {/* ENDEREÇO */}
              <div>
                <SubTitle>Endereço</SubTitle>
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <Button type="button" size="sm" variant="outline"
                    className="h-7 text-[11px] border-teal-200 text-teal-700 hover:bg-teal-50"
                    data-testid="single-apply-address-guardians"
                    onClick={() => { set('apply_aluno_address_to_responsibles', true); toast.success('O endereço do aluno será aplicado aos responsáveis ao salvar.'); }}>
                    <Home className="w-3.5 h-3.5 mr-1.5" /> Aplicar endereço do aluno aos responsáveis
                  </Button>
                  {form.apply_aluno_address_to_responsibles && (
                    <button type="button" className="text-[10.5px] text-slate-500 hover:underline"
                      data-testid="single-apply-address-cancel"
                      onClick={() => set('apply_aluno_address_to_responsibles', false)}>
                      cancelar
                    </button>
                  )}
                  <span className="text-[10.5px] text-slate-400">O endereço do aluno é a fonte principal da família.</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">CEP<Origem field="zip_code" /></Label>
                    <Input
                      value={form.zip_code || ''}
                      onChange={e => set('zip_code', maskCEP(e.target.value))}
                      onBlur={async (e) => {
                        const data = await fetchCEP(e.target.value);
                        if (!data) return;
                        setForm(f => ({
                          ...f,
                          address: f.address || data.address,
                          neighborhood: f.neighborhood || data.neighborhood,
                          city: f.city || data.city,
                          state: f.state || data.state,
                        }));
                        toast.success('Endereço preenchido pelo CEP');
                      }}
                      placeholder="00000-000"
                      data-testid="single-cep"
                    />
                    <p className="text-[10.5px] text-slate-400 mt-0.5">Preenche logradouro, bairro, cidade e UF</p>
                  </div>
                  <div className="lg:col-span-2">
                    <Label className="text-xs font-semibold text-slate-700">Endereço<Origem field="address" /></Label>
                    <Input value={form.address || ''} onChange={e => set('address', e.target.value)} placeholder="Rua / Avenida" data-testid="single-address" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Número</Label>
                    <Input value={form.address_number || ''} onChange={e => set('address_number', e.target.value)} placeholder="123" data-testid="single-address-number" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Complemento</Label>
                    <Input value={form.complement || ''} onChange={e => set('complement', e.target.value)} placeholder="Apto / Bloco" data-testid="single-complement" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Bairro<Origem field="neighborhood" /></Label>
                    <Input value={form.neighborhood || ''} onChange={e => set('neighborhood', e.target.value)} data-testid="single-neighborhood" />
                  </div>
                  <div className="lg:col-span-2">
                    <Label className="text-xs font-semibold text-slate-700">Cidade<Origem field="city" /></Label>
                    <Input value={form.city || ''} onChange={e => set('city', e.target.value)} data-testid="single-city" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Estado / UF</Label>
                    <Input value={form.state || ''} maxLength={2} onChange={e => set('state', e.target.value.toUpperCase())} placeholder="SP" data-testid="single-state" />
                  </div>
                </div>
              </div>

              {/* DOCUMENTOS */}
              <div>
                <SubTitle>Documentos</SubTitle>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">CPF<Origem field="cpf" /></Label>
                    <Input value={form.cpf || ''} onChange={e => set('cpf', maskCPF(e.target.value))} placeholder="000.000.000-00" data-testid="single-cpf" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">RG<Origem field="rg" /></Label>
                    <Input value={form.rg || ''} onChange={e => set('rg', e.target.value)} data-testid="single-rg" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Órgão emissor</Label>
                    <Input value={form.rg_orgao || ''} onChange={e => set('rg_orgao', e.target.value.toUpperCase())} placeholder="SSP/SP" maxLength={10} data-testid="single-rg-orgao" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Data de emissão do RG</Label>
                    <Input type="date" value={(form.rg_data_exp || '').slice(0, 10)} onChange={e => set('rg_data_exp', e.target.value)} data-testid="single-rg-data" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">RA / registro escolar</Label>
                    <Input value={form.ra || ''} onChange={e => set('ra', e.target.value)} data-testid="single-ra" />
                  </div>
                </div>
              </div>

              {/* OUTROS */}
              <div>
                <SubTitle>Outros</SubTitle>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-4 gap-y-3">
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Foto do aluno</Label>
                    <StudentPhotoUpload studentId={form.id} photoUrl={form.photo_url}
                      onUploaded={(url) => set('photo_url', url)} />
                  </div>
                  <div className="lg:col-span-2">
                    <Label className="text-xs font-semibold text-slate-700">Observações gerais</Label>
                    <Textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} rows={2} placeholder="Informações relevantes para a secretaria" data-testid="single-notes" />
                  </div>
                </div>
              </div>
            </div>
          </Block>

          {/* 2 — CONTRATOS E MATRÍCULAS (histórico real) · contém valores → só com acesso financeiro */}
          {mode === 'edit' && student?.id && financeiroVisivel && (
            <Block
              block={B.contratos}
              openState={openBlocks.contratos}
              onToggle={() => setOpenBlocks(s => ({ ...s, contratos: !s.contratos }))}
              innerRef={el => (blockRefs.current.contratos = el)}
            >
              <ContratosBlock
                studentId={student.id}
                onOpenFinanceiro={(cid) => { setFocusContractId(`${cid}#${Date.now()}`); goToBlock('financeiro'); }}
              />
            </Block>
          )}

          {/* 3 — SAÚDE E SEGURANÇA */}
          <Block
            block={B.saude}
            openState={openBlocks.saude}
            onToggle={() => setOpenBlocks(s => ({ ...s, saude: !s.saude }))}
            innerRef={el => (blockRefs.current.saude = el)}
            badge={statusBadge(!!(form.blood_type || form.allergies || form.diseases), 'Sem informações')}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
              <div>
                <Label className="text-xs font-semibold text-slate-700">Tipo sanguíneo</Label>
                <Select value={form.blood_type || ''} onValueChange={v => set('blood_type', v)}>
                  <SelectTrigger data-testid="single-blood-type"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Deficiência / necessidade específica</Label>
                <Input value={form.disabilities || ''} onChange={e => set('disabilities', e.target.value)} placeholder="Ex.: TEA nível 1, baixa visão…" data-testid="single-disabilities" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Alergias</Label>
                <Textarea value={form.allergies || ''} onChange={e => set('allergies', e.target.value)} rows={2} placeholder="Alimentos, medicamentos, picadas…" data-testid="single-allergies" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Doenças / condições</Label>
                <Textarea value={form.diseases || ''} onChange={e => set('diseases', e.target.value)} rows={2} placeholder="Asma, diabetes, epilepsia…" data-testid="single-diseases" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs font-semibold text-slate-700">Primeiros socorros / medicações</Label>
                <Textarea value={form.first_aid_notes || ''} onChange={e => set('first_aid_notes', e.target.value)} rows={2} placeholder="O que fazer em caso de emergência, medicação e dosagem" data-testid="single-first-aid" />
              </div>
            </div>
          </Block>

          {/* 5 — PEI E PEDAGÓGICO */}
          <Block
            block={B.pei}
            openState={openBlocks.pei}
            onToggle={() => setOpenBlocks(s => ({ ...s, pei: !s.pei }))}
            innerRef={el => (blockRefs.current.pei = el)}
            badge={statusBadge(!!(form.pedagogical_notes || '').trim(), 'Sem registro')}
          >
            <div className="pt-4 space-y-4">
              <HistoricoCard
                data={historico}
                loading={historicoLoading}
                dismissed={historicoDismissed}
                hasNotes={!!(form.pedagogical_notes || '').trim()}
                onApply={applyHistorico}
                onDismiss={() => setHistoricoDismissed(true)}
                mode={mode}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Classificação</Label>
                  <Input value={form.classification || ''} onChange={e => set('classification', e.target.value)} placeholder="Bolsista, conveniado…" data-testid="single-classification" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Número de chamada</Label>
                  <Input type="number" value={form.call_number ?? ''} onChange={e => set('call_number', e.target.value ? Number(e.target.value) : null)} data-testid="single-call-number" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Permanência — entrada</Label>
                  <Input type="time" value={form.permanence_entry || ''} onChange={e => set('permanence_entry', e.target.value)} data-testid="single-permanence-entry" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Permanência — saída</Label>
                  <Input type="time" value={form.permanence_exit || ''} onChange={e => set('permanence_exit', e.target.value)} data-testid="single-permanence-exit" />
                </div>
                <div className="sm:col-span-2 lg:col-span-4">
                  <Label className="text-xs font-semibold text-slate-700">PEI · Plano Educacional Individualizado e observações pedagógicas</Label>
                  <Textarea
                    value={form.pedagogical_notes || ''} onChange={e => set('pedagogical_notes', e.target.value)}
                    rows={6} placeholder="Objetivos, adaptações, apoios, histórico escolar anterior, autorizações…"
                    data-testid="single-pedagogical-notes"
                  />
                </div>
              </div>
            </div>
          </Block>

          {/* 5.1 — OCORRÊNCIAS PEDAGÓGICAS (histórico do aluno) */}
          {mode === 'edit' && student?.id && (
            <Block
              block={B.ocorrencias}
              openState={openBlocks.ocorrencias}
              onToggle={() => setOpenBlocks(s => ({ ...s, ocorrencias: !s.ocorrencias }))}
              innerRef={el => (blockRefs.current.ocorrencias = el)}
            >
              <div className="pt-4">
                <OcorrenciasPanel studentId={student.id} compact />
              </div>
            </Block>
          )}

          {/* 6 — RESPONSÁVEIS (mesmo conceito/funcionamento de hoje) */}
          <Block
            block={B.responsaveis}
            openState={openBlocks.responsaveis}
            onToggle={() => setOpenBlocks(s => ({ ...s, responsaveis: !s.responsaveis }))}
            innerRef={el => (blockRefs.current.responsaveis = el)}
            badge={statusBadge(!!form.responsible_id, 'Financeiro obrigatório')}
          >
            <div className="pt-4">
              <ResponsaveisStep
                form={form}
                set={set}
                errors={errors}
                responsibles={responsibles}
                onResponsiblesUpdate={onResponsiblesUpdate}
              />
            </div>
          </Block>

          {/* 7 — FINANCEIRO (oculto para perfis sem acesso financeiro) */}
          {financeiroVisivel && (
          <Block
            block={B.financeiro}
            openState={openBlocks.financeiro}
            onToggle={() => setOpenBlocks(s => ({ ...s, financeiro: !s.financeiro }))}
            innerRef={el => (blockRefs.current.financeiro = el)}
            badge={statusBadge((form.financial_plans || []).length > 0, 'Sem plano')}
          >
            <div className="pt-4 space-y-5">
              {mode === 'edit' && student?.id && (
                <FinanceiroContratosBlock studentId={student.id} focusContractId={focusContractId} />
              )}
              <details className="rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid="fin-planos-curso-legado">
                <summary className="text-[11px] font-bold uppercase tracking-wider text-slate-500 cursor-pointer">
                  Planos disponíveis do curso · vincular ao cadastro
                </summary>
                <div className="pt-3">
                  <FinanceiroStep
                    form={form}
                    set={set}
                    classGroups={classGroups}
                    mode={mode}
                    studentId={student?.id}
                  />
                </div>
              </details>
            </div>
          </Block>
          )}

          <div className="h-2" />
        </div>

        {/* ═══ RODAPÉ FIXO ═══ */}
        <div className="shrink-0 border-t border-slate-200 bg-white/95 backdrop-blur px-5 sm:px-7 py-3 flex items-center justify-between gap-3 shadow-[0_-6px_18px_rgba(15,23,42,.05)]">
          <div className="hidden sm:flex items-center gap-3 min-w-0">
            <span className="text-[11px] text-slate-500 hidden sm:block">
              Campos com <span className="text-rose-500 font-bold">*</span> são obrigatórios: nome completo, matrícula, data de nascimento e e-mail.
            </span>
            {mode === 'new' && draftSaved && (
              <span className="inline-flex items-center gap-1 text-[10.5px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5 shrink-0" data-testid="single-draft-indicator">
                <CheckCircle2 className="w-3 h-3" /> Rascunho salvo
              </span>
            )}
            {mode === 'new' && draftSaved && (
              <button
                type="button"
                onClick={() => { try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ } setForm(emptyForm()); setDraftSaved(false); toast.success('Rascunho descartado'); }}
                className="inline-flex items-center gap-1 text-[10.5px] text-slate-500 hover:text-rose-600 shrink-0"
                data-testid="single-draft-discard"
              >
                <Trash2 className="w-3 h-3" /> Descartar
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto justify-end shrink-0">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving} className="text-slate-500 hover:text-slate-800" data-testid="single-cancel">
              <X className="w-4 h-4 mr-1" /> Cancelar
            </Button>
            <Button
              onClick={handleSave} disabled={saving}
              className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 shadow-md shadow-emerald-500/25 px-6"
              data-testid="single-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Salvar cadastro</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

      {(form.id || student?.id) && (
        <SmartImportModal
          open={importOpen}
          onOpenChange={setImportOpen}
          studentId={form.id || student?.id}
          studentName={form.name || student?.name}
          onApplied={handleReload}
        />
      )}
      {mode === 'edit' && student?.id && (
        <DocumentosDnaSignDialog
          open={docsOpen}
          onOpenChange={setDocsOpen}
          student={{ id: student.id, name: form.name || student.name, registration_number: form.registration_number || student.registration_number }}
          onGoToField={goToBlock}
        />
      )}
    </>
  );
}
